/**
 * Automations — prompts that run unattended, once at a set time, every few
 * hours, or on a calendar, and file their results as chats; plus connected-app
 * sends (an email at 15:44) approved in chat and carried out word for word.
 *
 * A port of Amoreg's Automations page: the list on the left (current, paused,
 * done), a create/edit panel that slides in on the right (a sheet on mobile),
 * and the same information per row — what it does, when it next runs, and
 * whether it is paused or finished.
 *
 * A stored automation's schedule sentence and next run come from the API. A
 * draft the API has not seen yet is described, validated and previewed with
 * the shared `@repo/shared/automationSchedule` module the API itself uses (see
 * `automationDraft.ts`), so the preview cannot promise a time the runner does
 * not keep.
 *
 * @module components/automations/AutomationsPage
 */
import { Slow } from "@lucasmarkes/hairline/react";
import {
  CalendarClockIcon,
  ChevronDownIcon,
  MessageSquarePlusIcon,
  PaperclipIcon,
  PauseIcon,
  PlayIcon,
  PlusIcon,
  RefreshCwIcon,
  SearchIcon,
  SendIcon,
  SlidersHorizontalIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { useStartChatWithText } from "../../hooks/useStartChatWithText";
import { cn } from "../../lib/utils";
import type { OrgProfileUnavailableReason } from "../../state/orgProfile";
import { useActiveOrg } from "../../state/orgs";
import {
  useAutomations,
  type AutomationAction,
  type AutomationInput,
  type AutomationView,
} from "../../state/automations";
import { Button } from "../ui/button";
import { HairlineArt } from "../ui/hairline-art";
import { Input } from "../ui/input";
import { InputGroup, InputGroupAddon, InputGroupInput } from "../ui/input-group";
import { Group, GroupSeparator } from "../ui/group";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "../ui/menu";
import { ScrollArea } from "../ui/scroll-area";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import { Textarea } from "../ui/textarea";
import { toastManager } from "../ui/toast";
import { useIsMobile } from "../../hooks/useMediaQuery";
import {
  approvedOnLabel,
  bucketAutomations,
  draftDiffers,
  draftFrom,
  draftSubmission,
  emptyDraft,
  nextRunPreview,
  rowKindOf,
  statusLine,
  summariseActionArguments,
  type Draft,
  type DraftSubmission,
} from "./automationDraft";
import { browserTimezone, SchedulePicker } from "./SchedulePicker";

const CREATE_VIA_CHAT_PROMPT =
  "I want to set up a personal scheduled automation. Ask me what it should do and when it " +
  "should run, then create it for me.";


const ROW_ICONS = {
  action: SendIcon,
  once: CalendarClockIcon,
  recurring: RefreshCwIcon,
} as const;

function AutomationRow({
  automation,
  isSelected,
  now,
  onSelect,
}: {
  readonly automation: AutomationView;
  readonly isSelected: boolean;
  readonly now: Date;
  readonly onSelect: (automation: AutomationView) => void;
}) {
  const Icon = ROW_ICONS[rowKindOf(automation)];
  return (
    <button
      type="button"
      className={cn(
        "-mx-3 flex w-[calc(100%+1.5rem)] cursor-pointer items-start gap-3 rounded-lg px-3 py-3.5 text-left transition-colors",
        isSelected ? "bg-accent/60" : "hover:bg-accent/40",
      )}
      onClick={() => onSelect(automation)}
    >
      <span className="relative mt-0.5 flex size-5 shrink-0 items-center justify-center text-muted-foreground">
        <Icon className="size-4" />
        {automation.enabled ? (
          <span
            aria-hidden
            className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-primary"
          />
        ) : null}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[15px] font-medium text-foreground">{automation.title}</div>
        <div className="mt-1 truncate text-sm text-muted-foreground">
          {statusLine(automation, now)}
        </div>
      </div>
    </button>
  );
}

function Section({
  automations,
  now,
  onSelect,
  selectedId,
  title,
}: {
  readonly automations: ReadonlyArray<AutomationView>;
  readonly now: Date;
  readonly onSelect: (automation: AutomationView) => void;
  readonly selectedId: string | null;
  readonly title: string;
}) {
  if (automations.length === 0) {
    return null;
  }
  return (
    <section className="mb-8">
      <h2 className="pb-1 text-base font-medium text-foreground">{title}</h2>
      <div className="divide-y divide-border/40 pt-1">
        {automations.map((automation) => (
          <AutomationRow
            key={automation.id}
            automation={automation}
            isSelected={automation.id === selectedId}
            now={now}
            onSelect={onSelect}
          />
        ))}
      </div>
    </section>
  );
}

function DetailRow({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="flex min-h-9 items-center justify-between gap-4">
      <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
      <div className="flex min-w-0 items-center justify-end">{children}</div>
    </div>
  );
}

/**
 * What a scheduled send will do, read-only: it was approved word for word in
 * chat, and the browser can pause, reschedule or delete it but never change it.
 */
function ActionSends({ action }: { readonly action: AutomationAction }) {
  const fields = summariseActionArguments(action.arguments);
  const attachments = action.attachments.length;
  return (
    <div className="mt-4 rounded-lg border border-border/60 px-3.5 py-3">
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        <SendIcon className="size-3.5 text-muted-foreground" />
        Sends
      </div>
      <p className="mt-1.5 text-sm text-foreground/90">{action.summary}</p>
      {fields.length > 0 ? (
        <dl className="mt-3 space-y-1.5 text-sm">
          {fields.map((field, index) => (
            <div
              key={`${field.label}-${index}`}
              className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-2"
            >
              <dt className="truncate text-muted-foreground">{field.label}</dt>
              <dd
                className={cn(
                  "min-w-0 text-foreground/90",
                  field.long ? "line-clamp-3 whitespace-pre-line break-words" : "truncate",
                )}
                title={field.value}
              >
                {field.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
      {attachments > 0 ? (
        <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
          <PaperclipIcon className="size-3.5" />
          {attachments === 1 ? "1 attachment" : `${attachments} attachments`}
        </p>
      ) : null}
      <p className="mt-3 text-xs text-muted-foreground">
        {approvedOnLabel(action.approvedAt)}. It sends exactly this at the scheduled time.
      </p>
    </div>
  );
}

function AutomationPanel({
  draft,
  onChange,
  onClose,
  onCreate,
  onDelete,
  onSave,
  onToggleEnabled,
  saving,
  selected,
  submission,
}: {
  readonly draft: Draft;
  readonly onChange: (draft: Draft) => void;
  readonly onClose?: (() => void) | undefined;
  readonly onCreate: () => void;
  readonly onDelete?: (() => void) | undefined;
  readonly onSave?: (() => void) | undefined;
  readonly onToggleEnabled?: (() => void) | undefined;
  readonly saving: boolean;
  readonly selected: AutomationView | null;
  readonly submission: DraftSubmission;
}) {
  const isEdit = selected !== null;
  const action = selected?.action ?? null;
  const isDirty = isEdit && draftDiffers(draft, selected);
  const preview = nextRunPreview(draft, selected, submission, new Date());
  const afterPreview =
    action !== null
      ? "It sends exactly what was approved."
      : "Each run opens as a chat you can read and continue.";

  return (
    <div className="flex h-full flex-col px-6 py-5">
      <div className="mb-6 flex items-center justify-end gap-2">
        {onClose ? (
          <Button
            variant="ghost"
            size="icon-sm"
            className="mr-auto text-muted-foreground"
            onClick={onClose}
          >
            <XIcon className="size-4" />
            <span className="sr-only">Close panel</span>
          </Button>
        ) : null}
        {isEdit ? (
          <>
            {/* A finished automation has nothing to resume: a new time turns it back on. */}
            {selected.finished ? null : (
              <Button variant="outline" size="sm" disabled={saving} onClick={onToggleEnabled}>
                {selected.enabled ? (
                  <>
                    <PauseIcon className="size-3.5" />
                    Pause
                  </>
                ) : (
                  <>
                    <PlayIcon className="size-3.5" />
                    Resume
                  </>
                )}
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-muted-foreground hover:text-destructive"
              disabled={saving}
              onClick={onDelete}
            >
              <Trash2Icon className="size-4" />
              <span className="sr-only">Delete automation</span>
            </Button>
            {isDirty ? (
              <Button size="sm" disabled={!submission.valid || saving} onClick={onSave}>
                {saving ? "Saving…" : "Save"}
              </Button>
            ) : null}
          </>
        ) : (
          <Button size="sm" disabled={!submission.valid || saving} onClick={onCreate}>
            {saving ? "Creating…" : "Create automation"}
          </Button>
        )}
      </div>

      {action !== null ? (
        <>
          <h2 className="text-xl font-medium text-foreground">{draft.title}</h2>
          <ActionSends action={action} />
        </>
      ) : (
        <>
          <Input
            aria-label="Automation title"
            placeholder="Automation title"
            className="border-0 px-0 text-xl font-medium shadow-none placeholder:text-muted-foreground/50 focus-visible:ring-0 dark:bg-transparent"
            value={draft.title}
            onChange={(event) => onChange({ ...draft, title: event.target.value })}
          />
          <Textarea
            aria-label="Automation prompt"
            placeholder="Add prompt e.g. summarise this week's regulatory changes for our jurisdictions"
            className="mt-1 min-h-28 resize-none border-0 px-0 text-sm shadow-none placeholder:text-muted-foreground/50 focus-visible:ring-0 dark:bg-transparent"
            value={draft.prompt}
            onChange={(event) => onChange({ ...draft, prompt: event.target.value })}
          />
        </>
      )}

      <div className="mt-6">
        <span className="text-sm text-muted-foreground">Details</span>
        <div className="mt-2 space-y-0.5">
          <DetailRow label="Repeats">
            <SchedulePicker
              key={selected?.id ?? "new"}
              schedule={draft.schedule}
              onChange={(schedule) => onChange({ ...draft, schedule })}
            />
          </DetailRow>
          {isEdit ? (
            <DetailRow label="Status">
              <span className="px-2 text-sm text-foreground/90">
                {selected.enabled ? "Running" : selected.finished ? "Finished" : "Paused"}
              </span>
            </DetailRow>
          ) : null}
        </div>
        {submission.scheduleError !== null ? (
          <p role="alert" className="mt-2 text-xs text-destructive">
            {submission.scheduleError}
          </p>
        ) : null}
      </div>

      <div className="mt-8 flex items-start gap-2.5 rounded-lg bg-muted/50 px-3.5 py-3">
        <CalendarClockIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          {preview !== null ? `${preview}${preview.endsWith(".") ? "" : "."} ` : null}
          {afterPreview}
        </p>
      </div>
    </div>
  );
}

function PanelSurface({
  children,
  isMobile,
  onClose,
  open,
  title,
}: {
  readonly children: ReactNode;
  readonly isMobile: boolean;
  readonly onClose: () => void;
  readonly open: boolean;
  readonly title: string;
}) {
  if (isMobile) {
    return (
      <Sheet
        open={open}
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
      >
        <SheetContent side="right" className="w-full overflow-y-auto p-0 sm:max-w-md">
          <SheetTitle className="sr-only">{title}</SheetTitle>
          {children}
        </SheetContent>
      </Sheet>
    );
  }
  if (!open) {
    return null;
  }
  return (
    <aside className="w-[22rem] shrink-0 overflow-y-auto border-l border-border/60 lg:w-[26rem] xl:w-[30rem]">
      {children}
    </aside>
  );
}

function EmptyState({ onCreate }: { readonly onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border/60 bg-card px-6 py-16 text-center">
      <HairlineArt figure={Slow} surface="card" className="mb-4 w-48 sm:w-56" />
      <h2 className="text-base font-medium text-foreground">No automations yet</h2>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">
        Schedule a prompt to run once at a set time, every few hours, or on a calendar — a daily
        regulatory digest, a weekly policy check — and the results arrive as private chats. Only you
        can see and manage your automations.
      </p>
      <Button className="mt-4" size="sm" onClick={onCreate}>
        <PlusIcon className="size-4" />
        New automation
      </Button>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-6">
      {[0, 1, 2].map((row) => (
        <div key={row} className="flex items-start gap-3">
          <div className="mt-1 size-4 animate-pulse rounded-full bg-muted" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-48 animate-pulse rounded bg-muted" />
            <div className="h-3.5 w-72 animate-pulse rounded bg-muted/70" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** What the empty page says for each reason nothing could be listed. */
interface EmptyPageState {
  readonly title: string;
  readonly detail: string;
  /** Whether trying again can help — a failed read, not a missing setup. */
  readonly retry: boolean;
}

const NO_ORG_STATE: EmptyPageState = {
  title: "No organisation selected",
  detail: "Pick an organisation whose data your personal automations will use.",
  retry: false,
};

/**
 * One state per reason the list could not be read — the reasons the server
 * names, not one sentence for all of them. A failed read says so and offers
 * a retry; a server without the Amoreg API, or a session without an Amoreg
 * identity, says what is missing in plain words.
 */
const UNAVAILABLE_STATES: Readonly<Record<OrgProfileUnavailableReason, EmptyPageState>> = {
  error: {
    title: "Automations couldn't be loaded",
    detail: "Something went wrong reading your schedules. Try again in a moment.",
    retry: true,
  },
  unconfigured: {
    title: "Automations aren't set up on this server",
    detail:
      "This workspace server has no connection to the Amoreg API, so schedules can't be read or run. Whoever runs it needs to set AMOREG_API_URL and AMOREG_API_SERVICE_TOKEN.",
    retry: false,
  },
  "no-identity": {
    title: "Sign in with Amoreg to use automations",
    detail:
      "Automations belong to your Amoreg account. This session isn't signed in through Amoreg, so there is nothing to show.",
    retry: false,
  },
  "idp-unconfigured": {
    title: "The Amoreg API can't reach the identity provider",
    detail:
      "The domain API has no identity-provider key (WORKOS_API_KEY), so account-bound reads fail. Whoever runs it needs to set it on the API deployment.",
    retry: false,
  },
};

export function AutomationsPage() {
  const timezone = useMemo(browserTimezone, []);
  const activeOrg = useActiveOrg();
  const isMobile = useIsMobile();
  const { automations, unavailableReason, isPending, refresh, save, setEnabled, remove } =
    useAutomations(activeOrg?.id ?? null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  // The row open *now*, read after an await: a save that finishes after the
  // person opened another automation must not write into that one's draft.
  const openIdRef = useRef<string | null>(null);
  useEffect(() => {
    openIdRef.current = selectedId;
  }, [selectedId]);
  const [draft, setDraft] = useState<Draft>(() => emptyDraft(timezone));
  const [search, setSearch] = useState("");
  const [panelOpen, setPanelOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const selected = selectedId
    ? (automations.find((automation) => automation.id === selectedId) ?? null)
    : null;

  const report = useCallback((error: string | null, success: string) => {
    toastManager.add(
      error === null ? { type: "success", title: success } : { type: "error", title: error },
    );
  }, []);

  const openCreate = useCallback(() => {
    setSelectedId(null);
    setDraft(emptyDraft(timezone));
    setPanelOpen(true);
  }, [timezone]);

  const openAutomation = useCallback((automation: AutomationView) => {
    setSelectedId(automation.id);
    setDraft(draftFrom(automation));
    setPanelOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setPanelOpen(false);
    setSelectedId(null);
    setDraft(emptyDraft(timezone));
  }, [timezone]);

  // Writes the prompt into the org's new-chat draft, then opens it.
  const startChat = useStartChatWithText(activeOrg?.id ?? null);
  const createViaChat = useCallback(() => {
    if (startChat) void startChat(CREATE_VIA_CHAT_PROMPT);
  }, [startChat]);

  // Re-read on every render: whether a one-off is still ahead depends on the clock.
  const submission = draftSubmission(draft, selected, new Date());

  const submit = useCallback(
    async (automationId?: string) => {
      const stored = automationId === undefined ? null : selected;
      const current = draftSubmission(draft, stored, new Date());
      if (!current.valid) return;
      let input: AutomationInput;
      if (stored === null) {
        if (current.schedule === null) return;
        input = {
          title: draft.title.trim(),
          prompt: draft.prompt.trim(),
          schedule: current.schedule,
        };
      } else {
        // Only what changed: a scheduled send's title and prompt are fixed, an
        // unchanged schedule would recompute the next run, and a finished
        // automation given a new time is turned back on with it.
        input = {
          ...(stored.action === null
            ? { title: draft.title.trim(), prompt: draft.prompt.trim() }
            : {}),
          ...(current.schedule !== null ? { schedule: current.schedule } : {}),
          ...(current.reenable ? { enabled: true } : {}),
        };
      }
      setSaving(true);
      const error = await save(input, automationId);
      setSaving(false);
      report(
        error,
        automationId === undefined
          ? "Automation created"
          : current.reenable
            ? "Automation scheduled again"
            : "Automation updated",
      );
      if (error !== null) return;
      if (automationId === undefined) {
        closePanel();
      } else if (openIdRef.current === automationId) {
        // Hold what was sent, so the panel is clean once the list refreshes.
        setDraft((previous) => ({
          title: stored?.action === null ? previous.title.trim() : previous.title,
          prompt: stored?.action === null ? previous.prompt.trim() : previous.prompt,
          schedule: current.schedule ?? previous.schedule,
        }));
      }
    },
    [closePanel, draft, report, save, selected],
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return automations;
    return automations.filter(
      (automation) =>
        automation.title.toLowerCase().includes(query) ||
        automation.prompt.toLowerCase().includes(query) ||
        (automation.action?.summary.toLowerCase().includes(query) ?? false),
    );
  }, [automations, search]);
  const sections = useMemo(() => bucketAutomations(filtered), [filtered]);
  const now = new Date();

  // No org means nothing to schedule against — and the automations atom stays
  // idle rather than failing, so the empty state has to be decided here.
  if (activeOrg === null || unavailableReason !== null) {
    const state = unavailableReason === null ? NO_ORG_STATE : UNAVAILABLE_STATES[unavailableReason];
    return (
      // The same frame as the loaded page: it must take the whole content
      // area (flex-1 beside the sidebar) for the card to sit in its middle.
      <div
        role={activeOrg === null ? "status" : "alert"}
        data-testid="automations-empty-state"
        className="flex h-full min-h-0 flex-1 items-center justify-center bg-background px-6"
      >
        <div className="flex w-full max-w-md flex-col items-center rounded-lg border border-dashed border-border/60 bg-card px-6 py-14 text-center">
          <span className="mb-3 flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <CalendarClockIcon className="size-5" />
          </span>
          <h2 className="text-base font-medium text-foreground">{state.title}</h2>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">{state.detail}</p>
          {state.retry ? (
            <Button
              size="sm"
              variant="outline"
              className="mt-5"
              disabled={isPending}
              onClick={refresh}
            >
              <RefreshCwIcon className={cn("size-4", isPending && "animate-spin")} />
              Try again
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-1 bg-background">
      <ScrollArea className="h-full min-w-0 flex-1">
        <div className="mx-auto w-full max-w-2xl px-4 pb-12 pt-6 sm:px-6 md:px-8">
          <header className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">Automations</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Your personal scheduled prompts — once at a set time, every few hours, or on a
                calendar. Only you can see and manage them; results arrive as private chats.
              </p>
            </div>
            {/* The workspace's split button: primary action on the left, the
                menu on the right, one group — the same shape as the file
                toolbar's download control. */}
            <Group aria-label="Create automation" className="shrink-0">
              <Button size="sm" className="cursor-pointer" onClick={createViaChat}>
                <MessageSquarePlusIcon className="size-4" />
                <span className="hidden sm:inline">Create via chat</span>
              </Button>
              <GroupSeparator />
              <Menu>
                <MenuTrigger
                  render={
                    <Button
                      aria-label="More ways to create"
                      size="icon-sm"
                      className="cursor-pointer"
                    />
                  }
                >
                  <ChevronDownIcon aria-hidden="true" className="size-4" />
                </MenuTrigger>
                <MenuPopup align="end" className="w-44">
                  <MenuItem onClick={openCreate}>
                    <SlidersHorizontalIcon className="size-4" />
                    Create manually
                  </MenuItem>
                </MenuPopup>
              </Menu>
            </Group>
          </header>

          <div className="relative mb-8">
            <InputGroup className="h-8">
              <InputGroupAddon className="text-muted-foreground">
                <SearchIcon className="size-3.5" />
              </InputGroupAddon>
              <InputGroupInput
                placeholder="Search automations"
                aria-label="Search automations"
                className="text-sm"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </InputGroup>
          </div>

          {isPending && automations.length === 0 ? (
            <ListSkeleton />
          ) : automations.length === 0 ? (
            <EmptyState onCreate={openCreate} />
          ) : filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No automations match your search.
            </p>
          ) : (
            <div>
              <Section
                title="Current"
                automations={sections.current}
                now={now}
                onSelect={openAutomation}
                selectedId={selectedId}
              />
              <Section
                title="Paused"
                automations={sections.paused}
                now={now}
                onSelect={openAutomation}
                selectedId={selectedId}
              />
              <Section
                title="Done"
                automations={sections.done}
                now={now}
                onSelect={openAutomation}
                selectedId={selectedId}
              />
            </div>
          )}
        </div>
      </ScrollArea>

      <PanelSurface
        isMobile={isMobile}
        onClose={closePanel}
        open={panelOpen}
        title={selected ? "Edit automation" : "Create automation"}
      >
        <AutomationPanel
          draft={draft}
          saving={saving}
          selected={selected}
          submission={submission}
          onChange={setDraft}
          onClose={isMobile ? undefined : closePanel}
          onCreate={() => void submit()}
          onSave={selected ? () => void submit(selected.id) : undefined}
          onDelete={
            selected
              ? async () => {
                  setSaving(true);
                  const error = await remove(selected.id);
                  setSaving(false);
                  report(error, "Automation deleted");
                  if (error === null) closePanel();
                }
              : undefined
          }
          onToggleEnabled={
            selected
              ? async () => {
                  setSaving(true);
                  const error = await setEnabled(selected.id, !selected.enabled);
                  setSaving(false);
                  report(error, selected.enabled ? "Automation paused" : "Automation resumed");
                }
              : undefined
          }
        />
      </PanelSurface>
    </div>
  );
}
